import { CardType } from '~types'
import styles from './Card.module.scss'
import cx from 'classnames'
import { NEXT_IMAGE_QUALITY } from '~constants'
import Image from 'next/image'
import Video from 'components/elements/Video/Video'
import { Button, Heading, Paragraph } from '~elements'
import { getBackgroundGradient } from 'utils/styles/getBackgroundGradient'
import { useWindowSize } from 'utils/hooks'
import { getGtmLinkPosition } from '~analyticsUtils'

const Card = ({
  backgroundColor,
  backgroundGradient,
  centerCopyOnMobile,
  ctas,
  ctaText,
  description,
  hasDescriptionListCheckMark,
  hasLargeFontSizes,
  hasOverrideStyles,
  hasCtaText,
  hasVideo,
  headline,
  image,
  mediaPosition,
  overrideBackgroundColor,
  overrideSubheadlineColor,
  readMore,
  stackMediaAndCopy,
  subheadline,
  subheadlineColor = 'light',
  cardsHeaderFontSize,
  video
}: CardType) => {
  const { isBreakpoint: isBelowMd } = useWindowSize('sm', '<')
  const isMediaRight = !stackMediaAndCopy && mediaPosition === 'right'
  const hasNoBackgroundStyle = backgroundColor === 'transparent' && backgroundGradient === 'transparent'
  const backgroundStyle = getBackgroundGradient({
    gradient: backgroundGradient,
    color: backgroundColor,
    overrideColor: hasOverrideStyles ? overrideBackgroundColor : null,
    direction: stackMediaAndCopy || isBelowMd ? 'vertical' : 'horizontal',
    isMediaRight: isMediaRight
  })
  const shouldRenderCTAButtons = ctas && ctas.length
  const shouldRenderCTAText = hasCtaText && ctaText?.html

  return (
    <div
      className={cx(
        styles['card__container'],
        styles[!stackMediaAndCopy && !isBelowMd ? 'card__container--horizontal' : 'card__container--vertical'],
        isMediaRight && !isBelowMd && styles['card__container--horizontal-reverse']
      )}
    >
      <div className={styles['card__media-wrapper']}>
        {!hasNoBackgroundStyle && <div className={styles['card__media-wrapper-gradient']} />}
        {hasVideo && video ? (
          <Video backgroundVideoXl={video} posterImageXl={image} autoPlay={true} videoLoop={true} />
        ) : (
          image && (
            <Image
              src={image?.url}
              alt={image?.altText ?? headline ?? ''}
              sizes='100vw'
              fill
              width={0}
              height={0}
              style={{
                objectFit: hasNoBackgroundStyle || stackMediaAndCopy ? 'contain' : 'cover',
                ...(hasNoBackgroundStyle && { objectPosition: 'top center' })
              }}
              quality={NEXT_IMAGE_QUALITY}
            />
          )
        )}
      </div>
      <div
        className={cx(
          styles['card__headline-wrapper'],
          styles[`card__headline-wrapper--${cardsHeaderFontSize}-headline`],
          centerCopyOnMobile && isBelowMd && styles['card__headline-wrapper-mobile-centered'],
          hasDescriptionListCheckMark && styles['card__headline-wrapper-check-mark-list'],
          hasLargeFontSizes && styles['card__headline-wrapper--has-large-font-sizes'],
          hasNoBackgroundStyle && styles['card__headline-wrapper--has-no-background-style']
        )}
        style={{
          ...(backgroundStyle && { background: backgroundStyle }),
          ...(backgroundGradient === 'frosted_glass' && { backdropFilter: 'blur(4px)' })
        }}
      >
        <div className={styles['card__headline-inner']}>
          {headline && (
            <Heading level={3} justify={isBelowMd && centerCopyOnMobile ? 'center' : 'left'} noMarginTop>
              {headline}
            </Heading>
          )}
          {subheadline && (
            <Heading
              level={5}
              levelDisplay='eyebrow'
              color={hasOverrideStyles ? overrideSubheadlineColor : subheadlineColor}
              justify={isBelowMd && centerCopyOnMobile ? 'center' : 'left'}
              noMarginTop
            >
              {subheadline}
            </Heading>
          )}

          {description?.html && (
            <Paragraph
              text={description}
              justify={isBelowMd && centerCopyOnMobile ? 'center' : 'left'}
              readMoreText={hasNoBackgroundStyle && readMore ? readMore : null}
              fontSize={isBelowMd || !hasLargeFontSizes ? 'md' : 'md-plus'}
            />
          )}

          {(shouldRenderCTAButtons || shouldRenderCTAText) && (
            <div className={styles['cta-container']}>
              {shouldRenderCTAButtons ? (
                <>
                  {ctas.map((button, index) => (
                    <Button
                      key={`${button.title}_${index}`}
                      {...button}
                      isFullWidth={isBelowMd && stackMediaAndCopy}
                      analytics={{
                        linkModule: 'card',
                        linkPosition: getGtmLinkPosition(ctas, index)
                      }}
                    />
                  ))}
                </>
              ) : (
                shouldRenderCTAText && (
                  <Paragraph
                    text={ctaText}
                    justify={isBelowMd && centerCopyOnMobile ? 'center' : 'left'}
                    readMoreText={null}
                    fontSize={isBelowMd || !hasLargeFontSizes ? 'md' : 'md-plus'}
                  />
                )
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default Card
